import { useApolloClient } from '@apollo/client';
import { message } from 'antd';
import { Formik } from 'formik';
import { pick } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type GiftVoucherModuleSpecsFragment } from '../../../../api/fragments/GiftVoucherModuleSpecs';
import {
    type UpdateGiftVoucherModuleMutation,
    type UpdateGiftVoucherModuleMutationVariables,
    UpdateGiftVoucherModuleDocument,
} from '../../../../api/mutations/updateGiftVoucherModule';
import Form from '../../../../components/fields/Form';
import useHandleError from '../../../../utilities/useHandleError';
import useValidator from '../../../../utilities/useValidator';
import validators from '../../../../utilities/validators';
import GiftVoucherModuleDetailsForm from './GiftVoucherModuleDetailsForm';

export type FormValues = UpdateGiftVoucherModuleMutationVariables['settings'];

export type GiftVoucherModuleDetailsProps = {
    module: GiftVoucherModuleSpecsFragment;
};

const validator = validators.compose(validators.requiredNonEmptyString('displayName'));

const GiftVoucherModuleDetails = ({ module }: GiftVoucherModuleDetailsProps) => {
    const { t } = useTranslation(['moduleDetails', 'giftVoucherModuleDetails']);

    const validate = useValidator(validator);

    const initialValues = useMemo(
        (): FormValues => ({
            ...pick(['displayName', 'paymentSettingsId'], module),
        }),
        [module]
    );

    const apolloClient = useApolloClient();

    const onSubmit = useHandleError<FormValues>(
        async values => {
            // submitting message
            message.loading({
                content: t('giftVoucherModuleDetails:messages.updateGiftVoucherDetailsSubmitting'),
                key: 'primary',
                duration: 0,
            });

            // submit creation
            await apolloClient
                .mutate<UpdateGiftVoucherModuleMutation, UpdateGiftVoucherModuleMutationVariables>({
                    mutation: UpdateGiftVoucherModuleDocument,
                    variables: {
                        moduleId: module.id,
                        settings: values,
                    },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            // inform about success
            message.success({
                content: t('giftVoucherModuleDetails:messages.updateGiftVoucherDetailsSuccessful'),
                key: 'primary',
            });
        },
        [apolloClient, module.id, t]
    );

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit }) => (
                <Form id="updateMainDetails" name="updateMainDetails" onSubmitCapture={handleSubmit}>
                    <GiftVoucherModuleDetailsForm module={module} />
                </Form>
            )}
        </Formik>
    );
};

export default GiftVoucherModuleDetails;
